import React, { useState, useRef } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import LinearProgress from '@mui/material/LinearProgress'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { useTranslation } from 'react-i18next'
import { cloneVoice } from '../../services/ttsApi'
import { useAuth, useAlert } from '../../App'
import { tokens } from '../../theme/tokens'

interface VoiceCloneDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const VoiceCloneDialog: React.FC<VoiceCloneDialogProps> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const auth = useAuth() as any
  const { setAlert } = useAlert() as any

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [cloning, setCloning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  // L2: validate both extension and MIME type
  const isValidAudio = (file: File) =>
    file.name.toLowerCase().endsWith('.wav') && file.type.startsWith('audio/')

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return
    const incomingArr = Array.from(incoming)

    // Check MIME type first
    const nonAudio = incomingArr.filter((f) => !f.type.startsWith('audio/'))
    if (nonAudio.length > 0) {
      setError(t('audioMimeError', 'Only audio files are accepted'))
      return
    }

    const wavFiles = incomingArr.filter(isValidAudio)
    if (wavFiles.length < incomingArr.length) {
      setError(t('wavOnlyError', 'Only .wav files are accepted'))
      return
    }
    setError(null)
    setFiles((prev) => {
      const combined = [...prev, ...wavFiles]
      if (combined.length > 5) {
        setError(t('maxFilesError', 'Maximum 5 files allowed'))
        return combined.slice(0, 5)
      }
      return combined
    })
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = () => {
    setDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    addFiles(e.dataTransfer.files)
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files)
    // Reset so same file can be re-added after removal
    e.target.value = ''
  }

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
    setError(null)
  }

  const handleClose = () => {
    if (cloning) return
    setName('')
    setDescription('')
    setFiles([])
    setError(null)
    setDragOver(false)
    onClose()
  }

  // L1: keyboard handler for drop zone — Enter or Space triggers file picker
  const handleDropZoneKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (cloning) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      fileInputRef.current?.click()
    }
  }

  const handleClone = async () => {
    if (!name.trim()) {
      setError(t('nameRequired', 'Voice name is required'))
      return
    }
    if (files.length === 0) {
      setError(t('filesRequired', 'At least one WAV file is required'))
      return
    }
    if (files.length > 5) {
      setError(t('maxFilesError', 'Maximum 5 files allowed'))
      return
    }

    setError(null)
    setCloning(true)
    try {
      const token = await auth.getToken()
      await cloneVoice(name.trim(), description.trim(), files, token)
      setAlert({ type: 'success', text: t('voiceCloneSuccess', 'Voice cloned successfully!') })
      handleClose()
      onSuccess()
    } catch (err: any) {
      setError(err.message || t('cloneError', 'Failed to clone voice'))
    } finally {
      setCloning(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="voice-clone-dialog-title">
      <DialogTitle id="voice-clone-dialog-title" sx={{ pr: 6 }}>
        {t('cloneVoice', 'Clone Voice')}
        <IconButton
          onClick={handleClose}
          disabled={cloning}
          aria-label={t('cancel', 'Cancel')}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* L3: indeterminate progress during upload (no progress callback available) */}
      {cloning && <LinearProgress sx={{ mx: 0 }} />}

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label={t('voiceName', 'Voice Name')}
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 100))}
            required
            disabled={cloning}
            inputProps={{ maxLength: 100 }}
            helperText={`${name.length}/100`}
            fullWidth
          />

          <TextField
            label={t('description', 'Description')}
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 500))}
            multiline
            rows={3}
            disabled={cloning}
            inputProps={{ maxLength: 500 }}
            helperText={`${description.length}/500`}
            fullWidth
          />

          {/* Dropzone — L1: focusable with keyboard support and visible focus ring */}
          <Box
            tabIndex={0}
            role="button"
            aria-label={t('dropWavFiles', 'Drop WAV files here or click to browse')}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !cloning && fileInputRef.current?.click()}
            onKeyDown={handleDropZoneKeyDown}
            sx={{
              border: '2px dashed',
              borderColor: dragOver ? tokens.colors.primary.darkText : 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: cloning ? 'not-allowed' : 'pointer',
              backgroundColor: dragOver ? 'rgba(0,125,138,0.05)' : 'transparent',
              transition: 'border-color 0.2s, background-color 0.2s',
              outline: 'none',
              '&:hover': {
                borderColor: cloning ? 'divider' : tokens.colors.primary.darkText,
              },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('dropWavFiles', 'Drop WAV files here or click to browse')}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {t('maxFilesHint', '1–5 .wav files, max 5 total')}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".wav,audio/wav,audio/x-wav"
              multiple
              hidden
              onChange={handleFileInput}
            />
          </Box>

          {files.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {files.map((file, index) => (
                <Chip
                  key={`${file.name}-${index}`}
                  label={file.name}
                  onDelete={() => handleRemoveFile(index)}
                  disabled={cloning}
                  size="small"
                  sx={{ maxWidth: 220 }}
                />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={cloning}>
          {t('cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleClone}
          disabled={cloning || !name.trim() || files.length === 0}
          sx={{
            background: tokens.gradients.tts,
            '&:hover': { background: `linear-gradient(135deg, ${tokens.colors.tts.light} 0%, ${tokens.colors.tts.main} 100%)` },
          }}
        >
          {cloning ? t('cloning', 'Cloning...') : t('cloneVoice', 'Clone Voice')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default VoiceCloneDialog
